import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
} from "@tanstack/react-query";
import type {
	AvailableDeckMode,
	BulkCreateDecksPayload,
	CopyDeckResponse,
	DeckFlashcardsStats,
	DeckFlashcardsStatsParams,
	DeckPreview,
	DeckStats,
	DeckStatsParams,
	DeckType,
	GradeFlashcardPayload,
	UseDecksBulkPayload,
	UserDecksParams,
} from "src/api/flashcardDecks";
import {
	copyAndUseDecks,
	copyDeck,
	createDecksBulk,
	type DeckCategory,
	type DeckCategoryFilter,
	type DeckCategoryOption,
	generateDeck,
	getAvailableDeckModes,
	getDeckFlashcardsStats,
	getDeckPreview,
	getDeckStats,
	getUserDecks,
	gradeFlashcard,
	restartKnowDontKnowDeck,
	searchDecks,
	updateDeckType,
} from "src/api/flashcardDecks";
import api from "src/newApi";

import { queryClient } from "src/queryClient";
import options from "./defaultOptions";

export const DecksSize = 9;

export interface DeckUser {
	id: number;
	username: string;
	avatarUrl: string | null;
}

export interface SearchDeck {
	id: number;
	createdAt: string;
	updatedAt: string;
	name: string;
	description: string;
	permission: "public" | "private";
	code: string;
	status: "active" | "inactive";
	userId: number;
	parentId: number | null;
	type: DeckType;
	specId: number | null;
	isCompanyDeck: boolean;
	// Tylko z /flashcards/decks/search (publiczne zestawy).
	category?: DeckCategory;
	lastSolvedAt: string | null;
	flashcardsCount: number;
	usedCount: number;
	flashcardsToResolveCount?: number;
	user?: DeckUser;
}

export interface SearchDecksResponse {
	items: SearchDeck[];
	totalCount: number;
	totalPages: number;
	page: number;
	hasNextPage: boolean;
	// Tylko z /flashcards/decks/search (publiczne zestawy).
	availableCategories?: DeckCategoryOption[];
}

export type DeckSortBy =
	| "popularity"
	| "createdAt"
	| "name"
	| "flashcardsToResolveCount";
export type DeckSortOrder = "asc" | "desc";

interface SearchDecksFilters {
	page: number;
	category?: DeckCategoryFilter;
	orderBy?: DeckSortBy;
	order?: DeckSortOrder;
}

// "all" jest domyślne po stronie backendu — nie wysyłamy parametru.
const categoryParam = (category?: DeckCategoryFilter) =>
	category && category !== "all" ? { category } : {};

export function useSearchDecks(
	searchValue: string,
	filters: SearchDecksFilters,
) {
	return useQuery<SearchDecksResponse>({
		queryKey: ["decks-search", searchValue, filters],

		queryFn: () => {
			return searchDecks(searchValue || undefined, {
				page: filters.page,
				take: DecksSize,
				...categoryParam(filters.category),
				...(filters.orderBy && { orderBy: filters.orderBy }),
				...(filters.order && { order: filters.order.toUpperCase() }),
			});
		},

		...options,
		gcTime: 60 * 1000,
	});
}

export function useGetUserDecks() {
	return useQuery({
		queryKey: ["decks"],

		queryFn: () => {
			return getUserDecks().then((data) => data.items ?? data);
		},

		...options,
	});
}

export function useSearchUserDecks(params: Omit<UserDecksParams, "page">) {
	return useInfiniteQuery<SearchDecksResponse>({
		queryKey: ["user-decks-search", params],
		queryFn: ({ pageParam }) => {
			return getUserDecks({
				...params,
				page: pageParam as number,
				take: DecksSize,
			});
		},
		initialPageParam: 0,
		...options,
		getNextPageParam: (lastPage) => {
			if (lastPage.hasNextPage) {
				return lastPage.page + 1;
			}
			return undefined;
		},
	});
}

export function useGetTags() {
	return useQuery({
		queryKey: ["flashcards-tags"],

		queryFn: () => {
			return api.getTags();
		},

		...options,
	});
}

export function useRemoveDecks() {
	return useMutation({
		mutationFn: (codes: string[]) => {
			return api.removeDecks(codes);
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["decks"] }),
				queryClient.invalidateQueries({ queryKey: ["user-decks-search"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search-infinite"] }),
			]);
			queryClient.removeQueries({ queryKey: ["folder"] });
			queryClient.removeQueries({ queryKey: ["folder-items"] });
			queryClient.removeQueries({ queryKey: ["folders"] });
		},
	});
}

export function useGetUserDeck(code: string | null) {
	return useQuery({
		queryKey: ["deck", code],

		queryFn: () => {
			return api.getDeck(code, ["tags", "flashcards"]);
		},

		...options,
		enabled: !!code,
	});
}

export function useUpdateDeckType() {
	return useMutation<void, unknown, { deckId: number; type: DeckType }>({
		mutationFn: ({ deckId, type }) => updateDeckType(deckId, type),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["deck"] });
			queryClient.invalidateQueries({ queryKey: ["deck-modes"] });
			queryClient.invalidateQueries({ queryKey: ["decks"] });
			queryClient.invalidateQueries({ queryKey: ["decks-search"] });
			queryClient.invalidateQueries({ queryKey: ["decks-search-infinite"] });
			queryClient.invalidateQueries({ queryKey: ["user-decks-search"] });
			queryClient.invalidateQueries({ queryKey: ["deck-preview"] });
		},
	});
}

export function useGetAvailableDeckModes(
	deckId: number | null,
	enabled = true,
) {
	return useQuery<AvailableDeckMode[]>({
		queryKey: ["deck-modes", deckId],
		queryFn: () => getAvailableDeckModes(deckId as number),
		...options,
		enabled: enabled && !!deckId,

		// Małe zapytanie, a lista zmienia się po każdej ocenie fiszki —
		// nie cache'ujemy, zawsze pobieramy świeżą przy otwarciu menu.
		staleTime: 0,

		gcTime: 0,
	});
}

export interface ChangeDeckModePayload {
	deckId: number;
	type: DeckType;
	reset: boolean;
}

export function useChangeDeckMode() {
	return useMutation<void, unknown, ChangeDeckModePayload>({
		mutationFn: async ({ deckId, type, reset }) => {
			await updateDeckType(deckId, type);
			if (reset) {
				await restartKnowDontKnowDeck(deckId);
			}
		},
		onSuccess: async () => {
			// wyczyść cache statystyk WSZYSTKICH decków (nie tylko oznacz jako stale)
			queryClient.removeQueries({ queryKey: ["deck-stats"] });
			queryClient.removeQueries({ queryKey: ["deck-flashcards-stats"] });
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["deck"] }),
				queryClient.invalidateQueries({ queryKey: ["deck-modes"] }),
				queryClient.invalidateQueries({ queryKey: ["deck-preview"] }),
				queryClient.invalidateQueries({ queryKey: ["decks"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search-infinite"] }),
				queryClient.invalidateQueries({ queryKey: ["user-decks-search"] }),
			]);
		},
	});
}

interface CachedFlashcard {
	id: number;
	front: string;
	back: string;
}

interface CachedDeckFlashcard {
	flashcard: CachedFlashcard;
}

// ["deck", "flashcard", code] — fiszka do powtórki
interface FlashcardToResolveCache {
	flashcard: CachedDeckFlashcard;
}

// ["deck", code] — zestaw z embedami flashcards
interface UserDeckCache {
	flashcards: CachedDeckFlashcard[];
}

interface DeckCacheEntry {
	code: string;
	[key: string]: unknown;
}

interface UpdateDeckPayload {
	code: string;
	name?: string;
	description?: string;
	permission?: string;
	tags?: unknown[];
	tagIds?: number[];
}

export function useUpdateFlashcard() {
	return useMutation({
		mutationFn: ({
			id,
			deckId,
			front,
			back,
		}: {
			id: number;
			deckId: number;
			front: string;
			back: string;
			code?: string;
		}) => {
			return api.updateFlashcard(id, { deckId, front, back });
		},
		onSuccess: async (_data, variables) => {
			queryClient.setQueryData<FlashcardToResolveCache>(
				["deck", "flashcard", variables.code],
				(data) => {
					if (!data) {
						return data;
					}

					return {
						...data,
						flashcard: {
							...data.flashcard,
							flashcard: {
								...data.flashcard.flashcard,
								back: variables.back,
								front: variables.front,
							},
						},
					};
				},
			);
			queryClient.setQueryData<UserDeckCache>(
				["deck", variables.code],
				(data) => {
					if (!data) {
						return data;
					}
					return {
						...data,
						flashcards: data.flashcards.map((f) => {
							if (f.flashcard.id === variables.id) {
								return {
									...f,
									flashcard: {
										...f.flashcard,
										back: variables.back,
										front: variables.front,
									},
								};
							}

							return f;
						}),
					};
				},
			);
			queryClient.setQueryData<DeckPreview>(
				["deck-preview", variables.deckId],
				(data) => {
					if (!data) {
						return data;
					}
					return {
						...data,
						flashcards: data.flashcards.map((f) =>
							f.id === variables.id
								? { ...f, front: variables.front, back: variables.back }
								: f,
						),
					};
				},
			);
		},
	});
}

export function useGetFlashcardsCountToResolve(code: string) {
	return useQuery({
		queryKey: ["deck", "flashcards_count", code],

		queryFn: () => {
			return api.getCountOfFlashcardsInDeckToResolve(code);
		},

		...options,
	});
}

export function useGetUserFlashcardForDeck(code: string, enabled = true) {
	return useQuery({
		queryKey: ["deck", "flashcard", code],

		// 404 = brak fiszki do powtórki → null (v3 robił to w onError przez setQueryData)
		queryFn: () =>
			api.getFlashcardToResolve(code).catch((err) => {
				if (err?.response?.status === 404) return null;
				throw err;
			}),

		...options,
		enabled,
	});
}

export function useResolveFlashcard(code?: string) {
	return useMutation<void, unknown, GradeFlashcardPayload>({
		mutationFn: (payload) => gradeFlashcard(payload),
		onSuccess: () => {
			if (code) {
				queryClient.invalidateQueries({
					queryKey: ["deck", "flashcard", code],
				});
			}
			queryClient.invalidateQueries({ queryKey: ["deck-modes"] });
			queryClient.invalidateQueries({ queryKey: ["decks"] });
			queryClient.invalidateQueries({ queryKey: ["user-decks-search"] });
			queryClient.invalidateQueries({ queryKey: ["decks-search"] });
			queryClient.invalidateQueries({ queryKey: ["decks-search-infinite"] });
			queryClient.invalidateQueries({ queryKey: ["deck-preview"] });
		},
	});
}

export function useCopyFlashcard() {
	return useMutation({
		mutationFn: ({ id, code }: { id: number; code: string }) => {
			return api.copyFlashcard(id, code);
		},
		onError: () => {
			return null;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["decks"] });
		},
	});
}

export function useRemoveFlashcard(code: string) {
	return useMutation({
		mutationFn: ({
			deckId,
			flashcardId,
		}: {
			deckId: number;
			flashcardId: number;
		}) => {
			return api.removeFlashcard(deckId, flashcardId);
		},
		onSuccess: async (_data, variables) => {
			queryClient.setQueryData<number>(
				["deck", "flashcards_count", code],
				(count) => {
					if (typeof count !== "number") {
						return count;
					}
					return count - 1;
				},
			);
			queryClient.setQueryData<UserDeckCache>(["deck", code], (deck) => {
				if (!deck) {
					return deck;
				}
				return {
					...deck,
					flashcards: deck.flashcards.filter(
						(d) => d.flashcard?.id !== variables.flashcardId,
					),
				};
			});
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["decks"] }),
				queryClient.invalidateQueries({ queryKey: ["deck", code] }),
				queryClient.invalidateQueries({
					queryKey: ["deck", "flashcard", code],
				}),
				queryClient.invalidateQueries({ queryKey: ["deck-preview"] }),
				queryClient.invalidateQueries({ queryKey: ["user-decks-search"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search-infinite"] }),
				queryClient.invalidateQueries({ queryKey: ["folder"] }),
				queryClient.invalidateQueries({ queryKey: ["folder-items"] }),
			]);
		},
	});
}

export function useAddFlashcards() {
	return useMutation({
		mutationFn: ({
			code,
			flashcards,
		}: {
			code: string;
			flashcards: { front: string; back: string }[];
		}) => {
			return api.addFlashcards(code, flashcards);
		},
		onSuccess: async (_data, variables) => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["decks"] }),
				queryClient.invalidateQueries({ queryKey: ["deck", variables.code] }),
				queryClient.invalidateQueries({
					queryKey: ["deck", "flashcard", variables.code],
				}),
				queryClient.invalidateQueries({
					queryKey: ["deck", "flashcards_count", variables.code],
				}),
				queryClient.invalidateQueries({ queryKey: ["deck-preview"] }),
				queryClient.invalidateQueries({ queryKey: ["user-decks-search"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search-infinite"] }),
				queryClient.invalidateQueries({ queryKey: ["folder"] }),
				queryClient.invalidateQueries({ queryKey: ["folder-items"] }),
			]);
		},
	});
}

export function useCreateDeck() {
	return useMutation({
		mutationFn: (data: Record<string, unknown>) => {
			return generateDeck(data);
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["decks"] }),
			]);
			queryClient.removeQueries({ queryKey: ["folder"] });
			queryClient.removeQueries({ queryKey: ["folder-items"] });
			queryClient.removeQueries({ queryKey: ["folders"] });
		},
	});
}

export function useCopyAndUseDecks() {
	return useMutation<CopyDeckResponse[], unknown, UseDecksBulkPayload>({
		mutationFn: (payload: UseDecksBulkPayload) => copyAndUseDecks(payload),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["decks"] });
			queryClient.invalidateQueries({ queryKey: ["user-decks-search"] });
			queryClient.removeQueries({ queryKey: ["folder"] });
			queryClient.removeQueries({ queryKey: ["folder-items"] });
			queryClient.removeQueries({ queryKey: ["folders"] });
		},
	});
}

export function useCreateDecksBulk() {
	return useMutation<string[], unknown, BulkCreateDecksPayload>({
		mutationFn: (payload: BulkCreateDecksPayload) => createDecksBulk(payload),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["decks"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search-infinite"] }),
				queryClient.invalidateQueries({ queryKey: ["user-decks-search"] }),
			]);
			queryClient.removeQueries({ queryKey: ["folder"] });
			queryClient.removeQueries({ queryKey: ["folder-items"] });
			queryClient.removeQueries({ queryKey: ["folders"] });
		},
	});
}

export function useUpdateDeck() {
	return useMutation({
		mutationFn: ({ code, tags, ...details }: UpdateDeckPayload) => {
			return api.updateDeck(code, details);
		},
		onSuccess: async (_data, { code, tagIds, tags, ...details }) => {
			queryClient.setQueryData<DeckCacheEntry[]>(["decks"], (decks) => {
				if (!Array.isArray(decks)) {
					return decks;
				}
				return decks.map((deck) => {
					if (deck.code === code) {
						return {
							...deck,
							...details,
							tags,
						};
					}

					return deck;
				});
			});
			queryClient.setQueryData<DeckCacheEntry>(["deck", code], (deck) => {
				if (!deck) {
					return deck;
				}
				return { ...deck, ...details, ...(tags ? { tags } : {}) };
			});
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["deck", code] }),
				queryClient.invalidateQueries({ queryKey: ["deck-preview"] }),
				queryClient.invalidateQueries({ queryKey: ["user-decks-search"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search-infinite"] }),
				queryClient.invalidateQueries({ queryKey: ["folder"] }),
				queryClient.invalidateQueries({ queryKey: ["folder-items"] }),
			]);
		},
	});
}

export function useGetDeckPreview(deckId: number | null) {
	return useQuery<DeckPreview>({
		queryKey: ["deck-preview", deckId],
		queryFn: () => getDeckPreview(deckId as number),
		...options,
		enabled: !!deckId,
	});
}

export interface CopyDeckPayload {
	deckId: number;
}

export function useCopyDeck() {
	return useMutation<CopyDeckResponse, unknown, CopyDeckPayload>({
		mutationFn: ({ deckId }: CopyDeckPayload) => copyDeck(deckId),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["decks-search"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search-infinite"] }),
				queryClient.invalidateQueries({ queryKey: ["user-decks-search"] }),
				queryClient.invalidateQueries({ queryKey: ["decks"] }),
			]);
		},
	});
}

export function useRestartKnowDontKnowDeck() {
	return useMutation<void, unknown, number>({
		mutationFn: (deckId: number) => restartKnowDontKnowDeck(deckId),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["deck"] }),
				queryClient.invalidateQueries({ queryKey: ["deck-stats"] }),
				queryClient.invalidateQueries({ queryKey: ["deck-flashcards-stats"] }),
				queryClient.invalidateQueries({ queryKey: ["deck-preview"] }),
				queryClient.invalidateQueries({ queryKey: ["decks"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search"] }),
				queryClient.invalidateQueries({ queryKey: ["decks-search-infinite"] }),
				queryClient.invalidateQueries({ queryKey: ["user-decks-search"] }),
			]);
		},
	});
}

export function useGetDeckStats(
	deckId: number | null,
	params?: DeckStatsParams,
) {
	return useQuery<DeckStats>({
		queryKey: [
			"deck-stats",
			deckId,
			params?.startDate,
			params?.endDate,
			params?.type,
		],
		queryFn: () => getDeckStats(deckId as number, params),
		...options,
		enabled: !!deckId,
		placeholderData: keepPreviousData,
	});
}

export function useGetDeckFlashcardsStats(
	deckId: number | null,
	params?: DeckFlashcardsStatsParams,
) {
	return useQuery<DeckFlashcardsStats>({
		queryKey: [
			"deck-flashcards-stats",
			deckId,
			params?.startDate,
			params?.endDate,
			params?.sort,
			params?.type,
		],

		queryFn: () => getDeckFlashcardsStats(deckId as number, params),
		...options,
		enabled: !!deckId,
	});
}
